/**
 * NutritionMixPreviewCard — N·P·K per-tank mix preview for a foliar application combo.
 *
 * Props:
 *   preview: NutritionMixPreview — from nutritionSessionService.getMixPreview(comboId)
 *
 * Renders a card with:
 *   - Machine name, tank count, and total water volume as a header/summary
 *   - A table with columns: Product | Dose per tank | N·P·K
 *   NOTE: shows N·P·K only — never PHI or intensity (foliar mix, not spray).
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { NutritionMixPreview, NutritionMixPreviewLine } from '../../services/nutritionSessionService';
import { formatMeasure } from '../../utils/formatMeasure';
import { DataTable } from '../patterns';
import type { DataTableColumn } from '../patterns';
import './nutrition.css';

interface NutritionMixPreviewCardProps {
  preview: NutritionMixPreview;
}

function fmtNPK(n: number, p: number, k: number): string {
  const fmt = (v: number) => (v != null ? String(v) : '—');
  return `${fmt(n)} / ${fmt(p)} / ${fmt(k)}`;
}

const round2 = (n: number): number => Math.round((n + Number.EPSILON) * 100) / 100;

const NutritionMixPreviewCard: React.FC<NutritionMixPreviewCardProps> = ({ preview }) => {
  const { t } = useTranslation();

  const tankVolume = preview.machine?.tankVolume;
  const doseColHeading = tankVolume
    ? t('nutrition.sessions.detail.mixPreview.colDoseVolume', { volume: tankVolume })
    : t('nutrition.sessions.detail.mixPreview.colDose');

  // Exact (unrounded) tank fraction, e.g. 1650 L / 3000 L tank = 0.55 tanks.
  // Only shown when it differs from the rounded-up tankCount — an exact
  // whole number is noise, not information.
  let tankCountDisplay: string = String(preview.tankCount);
  if (tankVolume && preview.totalWaterL) {
    const exact = preview.totalWaterL / tankVolume;
    if (Number.isFinite(exact) && round2(exact) !== preview.tankCount) {
      tankCountDisplay = t('nutrition.sessions.detail.mixPreview.tankCountExact', {
        count: preview.tankCount,
        exact: round2(exact),
      });
    }
  }

  // ONE render function for the dose cell, used by both the table column and
  // the phone card. Those two have drifted apart three times in this feature,
  // always by one of them being edited alone (#456 and two repeats), so they
  // share a function rather than a copied expression. The testid is a
  // PARAMETER because jsdom renders both copies simultaneously and a shared
  // id makes getByTestId ambiguous.
  const doseFor = (line: NutritionMixPreviewLine) => formatMeasure(line.dosePerTank, line.doseUnit);

  const columns: DataTableColumn<NutritionMixPreviewLine>[] = [
    { key: 'productName', label: t('nutrition.sessions.detail.mixPreview.colProduct') },
    {
      key: 'dose',
      // The tank volume lives in the HEADING, not beside each number: "per
      // tank" against an unnamed machine is the ambiguity that made these
      // figures unreadable in the field.
      label: doseColHeading,
      align: 'right',
      render: (line) => doseFor(line),
    },
    {
      key: 'npk',
      label: t('nutrition.sessions.detail.mixPreview.colNPK'),
      render: (line) => <span className="ntr-mix-table__npk">{fmtNPK(line.n, line.p, line.k)}</span>,
    },
  ];

  return (
    <div className="ntr-card" data-testid="mix-preview-card">
      <div className="ntr-card__header">
        <h3 className="ntr-card__title">{t('nutrition.sessions.detail.mixPreview.title')}</h3>
      </div>
      <div className="ntr-card__body">
        {/* Machine + tank summary */}
        <div className="ntr-meta-grid" style={{ marginBottom: 12 }}>
          <div className="ntr-meta-grid__item">
            <span className="ntr-meta-grid__label">
              {t('nutrition.sessions.detail.mixPreview.machine')}
            </span>
            <span className="ntr-meta-grid__value">{preview.machine.name}</span>
          </div>
          <div className="ntr-meta-grid__item">
            <span className="ntr-meta-grid__label">
              {t('nutrition.sessions.detail.mixPreview.tankCount')}
            </span>
            <span className="ntr-meta-grid__value" data-testid="tank-count-value">
              {tankCountDisplay}
            </span>
          </div>
          <div className="ntr-meta-grid__item">
            <span className="ntr-meta-grid__label">
              {t('nutrition.sessions.detail.mixPreview.totalWater')}
            </span>
            <span className="ntr-meta-grid__value">
              {preview.totalWaterL} L
            </span>
          </div>
        </div>

        {/* Per-tank mix table */}
        <div data-testid="mix-table">
          <DataTable<NutritionMixPreviewLine>
            columns={columns}
            rows={preview.perTankMix}
            rowKey={(line) => line.productId}
            emptyState={t('nutrition.sessions.detail.mixPreview.empty')}
            // Three columns, and this card renders on the operator's phone
            // at three call sites in NutritionFoliarFieldPage. Patterns.css
            // display:none's the whole table below 768px once mobileCardRow
            // exists, so the card must carry the dose — it is the number
            // being poured.
            mobileCardRow={(line) => (
              <div className="ntr-mix-card" data-testid={`mix-card-${line.productId}`}>
                <span className="ntr-mix-card__name">{line.productName}</span>
                <span className="ntr-mix-card__dose">{doseFor(line)}</span>
                <span className="ntr-mix-card__heading">{doseColHeading}</span>
                <span className="ntr-mix-card__npk">{fmtNPK(line.n, line.p, line.k)}</span>
              </div>
            )}
          />
        </div>
      </div>
    </div>
  );
};

export default NutritionMixPreviewCard;
